import { Fragment, type ReactNode } from 'react';

/**
 * The assistant's answers arrive as light Markdown — `**bold**`, `` `code` ``,
 * `*italic*`, `–`/`-`/`•` bullet lines and blank-line paragraphs. The panel used
 * to print the raw string, so a person saw the asterisks and backticks. This
 * renders that subset to React nodes only — no `dangerouslySetInnerHTML`, so
 * nothing the model writes can inject markup. Anything it does not recognise is
 * shown verbatim, which is the safe failure.
 *
 * A dependency (react-markdown) would be heavier and pull raw-HTML surface the
 * CSP is there to avoid; the assistant's output is a small, known subset, so a
 * purpose-built renderer is the honest fit. See `AssistantMessage.spec`.
 */

/** Inline: **bold**, `code`, *italic*. Emitted in that order so `**` wins over `*`. */
export function renderInline(text: string, keyBase = 'i'): ReactNode[] {
  // One regex, three alternatives; each match becomes an element, the gaps text.
  const re = /\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*/g;
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let n = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyBase}-${n++}`;
    if (m[1] !== undefined) out.push(<strong key={key}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<code key={key}>{m[2]}</code>);
    else out.push(<em key={key}>{m[3]}</em>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const BULLET = /^\s*[-–•]\s+(.*)$/;

/**
 * Blocks split on blank lines; within a block, a run of bullet lines becomes a
 * list and a run of plain lines a paragraph — so a lead sentence followed by
 * bullets on the next lines (no blank line between, which is how the model
 * writes it) renders as prose + list, not one flat paragraph.
 */
export function renderMarkdown(content: string): ReactNode[] {
  const blocks = content.replace(/\r\n/g, '\n').split(/\n{2,}/);
  const nodes: ReactNode[] = [];
  blocks.forEach((block, bi) => {
    const lines = block.split('\n').filter((l) => l.trim() !== '');
    let run: { bullet: boolean; lines: string[] } | null = null;
    let ri = 0;
    const flush = () => {
      if (!run) return;
      const key = `${bi}-${ri++}`;
      if (run.bullet) {
        nodes.push(
          <ul key={`ul-${key}`} className="asp__md-list">
            {run.lines.map((l, li) => (
              <li key={li}>{renderInline(l.replace(BULLET, '$1'), `b${key}-${li}`)}</li>
            ))}
          </ul>,
        );
      } else {
        nodes.push(
          <p key={`p-${key}`} className="asp__md-p">
            {run.lines.map((l, li) => (
              <Fragment key={li}>
                {li > 0 ? <br /> : null}
                {renderInline(l, `p${key}-${li}`)}
              </Fragment>
            ))}
          </p>,
        );
      }
      run = null;
    };
    for (const line of lines) {
      const bullet = BULLET.test(line);
      if (!run || run.bullet !== bullet) {
        flush();
        run = { bullet, lines: [] };
      }
      run.lines.push(line);
    }
    flush();
  });
  return nodes;
}

/** One assistant/user bubble. User text is shown verbatim; the agent is Markdown. */
export function AssistantMessage({
  content,
  role,
}: {
  content: string;
  role: string;
}): React.JSX.Element {
  if (role !== 'agent') return <>{content}</>;
  return <>{renderMarkdown(content)}</>;
}
